import {
	Document,
	Paragraph,
	HeadingLevel,
	TextRun,
	Packer,
	AlignmentType,
} from 'docx';
import { DateTime } from 'luxon';
import { saveAs } from 'file-saver';

const VIEWS_API_ROOT = '/wp-json/views-plugin/v1/';

const DOCUMENT_DEFAULT_STYLES = {
	heading1: {
		run: { size: 36, bold: true },
		paragraph: { spacing: { after: 100 } },
	},
};

const compact = arr => arr.filter(Boolean);

function sortBySessionDateTime(a, b) {
	if (a.session_date < b.session_date) return -1;
	if (a.session_date > b.session_date) return 1;
	if (a.session_start_time < b.session_start_time) return -1;
	if (a.session_start_time > b.session_start_time) return 1;
	return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

const formatDateTime = (inputFmt, outputFmt) => str =>
	DateTime.fromFormat(str, inputFmt).toFormat(outputFmt);

const formatSessionTime = formatDateTime('HH:mm', 'h:mm a');
const formatSessionDate = formatDateTime('yyyy-LL-dd', 'MMMM d, y');

const textAfterBreak = (text = '', options = {}) =>
	text ? new TextRun({ ...options, text, break: 1 }) : undefined;

const mapSessionSpeakers = (speakers = []) => {
	if (!speakers.length) return [];
	return [
		new Paragraph({ text: 'Speakers:', heading: HeadingLevel.HEADING_3, spacing: { after: 100 } }),
		...speakers.map(s =>
			new Paragraph({
				children: compact([
					new TextRun({ text: s.name, bold: true }),
					s.speaker_categories && s.speaker_categories.length
						? new TextRun(` (${s.speaker_categories.join(', ')})`)
						: undefined,
					textAfterBreak(s.title),
					textAfterBreak(s.company),
				]),
				spacing: { after: 200 },
			})
		),
	];
};

const mapSessionSponsors = (sponsors = []) => {
	if (!sponsors.length) return [];
	return [
		new Paragraph({ text: 'Sponsors:', heading: HeadingLevel.HEADING_3, spacing: { after: 100 } }),
		...sponsors.map(s =>
			new Paragraph({
				children: [new TextRun({ text: s.name, bold: true })],
				spacing: { after: 200 },
			})
		),
	];
};

const underlineLabel = (label, value) =>
	new Paragraph({
		children: [
			new TextRun({ text: label, underline: {} }),
			new TextRun(': '),
			new TextRun(value),
		],
	});

function generateAgendaDocx({ documentTitle = 'agenda', filename = 'agenda.docx', sessions = [] }) {
	Packer.toBlob(
		new Document({
			styles: { default: DOCUMENT_DEFAULT_STYLES },
			sections: [{
				properties: {},
				children: [
					new Paragraph({
						text: documentTitle,
						heading: HeadingLevel.HEADING_1,
						alignment: AlignmentType.CENTER,
						spacing: { after: 200 },
					}),
					...sessions.sort(sortBySessionDateTime).flatMap(({
						name,
						session_date,
						session_start_time,
						session_end_time,
						description_no_html,
						session_speakers,
						session_location,
						session_sponsors,
						trax,
					}) => {
						let text = '';
						if (session_date) {
							text += formatSessionDate(session_date);
							if (session_start_time) text += ' ';
						}
						if (session_start_time) {
							text += formatSessionTime(session_start_time);
							if (session_end_time) text += ' – ';
						}
						if (session_end_time) text += formatSessionTime(session_end_time);

						return [
							new Paragraph({ heading: HeadingLevel.HEADING_2, spacing: { after: 100, before: 500 }, text }),
							new Paragraph({
								children: [new TextRun({ text: name, bold: true })],
								heading: HeadingLevel.HEADING_2,
								spacing: { after: 100 },
							}),
							...(session_location ? [underlineLabel('Room', session_location)] : []),
							...(trax && trax.length ? [underlineLabel('Tracks', trax.map(t => t.name).join(', '))] : []),
							new Paragraph({ text: description_no_html }),
							...mapSessionSpeakers(session_speakers),
							...mapSessionSponsors(session_sponsors),
						];
					}),
				],
			}],
		})
	).then(blob => saveAs(blob, filename));
}

(function ($) {
	$(document).on('click', '.download-views-agenda', function (e) {
		e.stopImmediatePropagation();

		const $btn = $(this);
		const eventId = $btn.data('event-id');
		const domain = $btn.data('api-domain') ?? '';
        const apiRoot = domain + VIEWS_API_ROOT;
		const originalText = $btn.text();

		$btn.prop('disabled', true).text('Downloading...');

		$.getJSON(apiRoot + 'agendadata/' + eventId)
			.done(function (data) {
				const payload = data.payload;
				generateAgendaDocx({
					documentTitle: payload.event_name,
					filename: payload.event_slug + '-agenda.docx',
					sessions: payload.sessions,
				});
			})
			.fail(function () {
				alert('Failed to download agenda. Please try again.');
			})
			.always(function () {
				$btn.prop('disabled', false).text(originalText);
			});
	});
})(jQuery);
